import { zodResolver } from '@hookform/resolvers/zod';
import { AlertsFormStateValues, schema } from 'modules/campaigns/lib/schemas/alertSchema';
import { FC, PropsWithChildren, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useProjectAlertsContext } from './ProjectAlertsContext';

export const AlertsFormProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { defaultValues } = useProjectAlertsContext();

  const methods = useForm<AlertsFormStateValues>({
    defaultValues,
    /**
     * Validation is initially triggered on the first blur event.
     * After that, it is triggered on every change event.
     * @see https://react-hook-form.com/docs/useform#mode:~:text=impact%20on%20performance.-,onTouched,-string
     */
    mode: 'onTouched',
    resolver: zodResolver(schema),
  });

  /**
   * Validate default values on mount.
   * This is especially done because `isDirty` is not set to true until the user interacts with the form,
   * but we want to allow users to submit the form with the default values.
   * @see https://github.com/react-hook-form/react-hook-form/issues/2755
   */
  useEffect(() => {
    methods.trigger();
  }, []);

  /**
   * Resets default values when changes occur
   */
  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues]);

  return <FormProvider {...methods}>{children}</FormProvider>;
};
